import { useMediaQuery } from '@vueuse/core';
// The breakpoints are declared in SCSS because that is where most of them are consumed; the
// `:export` block is what makes them readable here, the same trick `AppPreset.ts` uses for the
// brand colour. Keeping one source means a media query and the composable mirroring it cannot
// disagree about where mobile starts.
import variables from '@/assets/_variables.module.scss';

/** `$bp-mobile` — at or below this the layout goes to a single column. */
const BP_MOBILE = variables.bpMobile;

/** `$bp-narrow` — at or below this a toolbar row has room for two controls, not three. */
const BP_NARROW = variables.bpNarrow;

/**
 * Whether the viewport is at or below `$bp-mobile`, live.
 *
 * Prefer this over `window.matchMedia(...).matches`: sampled once at setup it never updates, so
 * rotating a tablet leaves the component laid out for the orientation it mounted in.
 */
export const useIsMobile = () => useMediaQuery(`(max-width: ${BP_MOBILE})`);

/**
 * Whether the viewport is at or below `$bp-narrow`, live.
 *
 * Narrower than mobile: the width of a small phone, where a label that reads as a sentence has to
 * shorten to a token or the row it sits in wraps. Same live-query reasoning as `useIsMobile`.
 */
export const useIsNarrow = () => useMediaQuery(`(max-width: ${BP_NARROW})`);

/** `$bp-short` — held sideways, at or below this height the screen is a phone's, not a tablet's. */
const BP_SHORT = variables.bpShort;

/**
 * The one query that says "a phone on its side", and the second clause of `useIsPhoneScreen`.
 *
 * Its own composable because the expanded graph needs the clause on its own as well as with the
 * other: below this height there is no room for a header bar *and* a control bar, so the two fold
 * into one and the controls move into the header. That is a shape change, not a size change, and
 * it does not happen in portrait however narrow the screen gets.
 */
export const useIsShortLandscape = () =>
  useMediaQuery(`(orientation: landscape) and (max-height: ${BP_SHORT})`);

/**
 * Whether this screen is a phone's, live — the viewport test the expanded graph goes full-screen
 * on, and the JS mirror of the two-clause media query in `DataGraphDialog.vue`.
 *
 * Two clauses, because neither catches what the other does. A landscape phone is around 844px
 * wide, so no width alone reaches it; a portrait phone is 600-900px tall, so no height alone
 * reaches that. Together they cover a phone in both orientations and leave tablets out: an iPad
 * is wider than `$bp-mobile` and, turned sideways, still 744-834px tall.
 *
 * Deliberately a *viewport* test, where the chart's own responsive rules are container ones.
 * `perfusionChartOption.ts`'s `COMPACT_CHART_PX` measures the box the chart was drawn into, and a
 * full-screen chart on a landscape phone is ~844px of it — past that threshold, so a compact-width
 * test would skip the very orientation this is for.
 */
export const useIsPhoneScreen = () =>
  useMediaQuery(
    `(max-width: ${BP_MOBILE}), (orientation: landscape) and (max-height: ${BP_SHORT})`
  );
